/**
 * WorkshopSessionInfoTab — MED-566 (E18-9), MED-568 (E18-10)
 * Session Info tab for the Workshop Detail Drawer.
 * Shows participants list (with + Add Participants) and resources.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Box,
  Badge,
  Loader,
  Center,
  Alert,
  Table,
  ThemeIcon,
  Modal,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { AlertCircle, Check, Plus, Users, X, UserMinus } from 'react-feather';
import { ParticipantAvatar } from '../../../../components/common';
import { getGroupAvatarColor } from '../../../../utils/groupColors';
import { workshopsApi, type WorkshopParticipant } from '../../../../services/api/workshops';
import { sessionsApi } from '../../../../services/api/sessions';
import { resourcesApi } from '../../../../services/api/resources';
import type { Session, ResourceCategory } from '../../../../types';
import { WithdrawnPill } from '../participants/ParticipantCellRenderers';
import { SectionAccordion } from './SectionAccordion';
import { ResourcesSection } from './ResourcesSection';
import { AddWorkshopParticipantsModal } from './AddWorkshopParticipantsModal';
import { AddResourceModal } from '../AddResourceModal';
// MED-1044 — shared RSVP pill config (was duplicated inline here).
import { RSVP_STATUS_CONFIG } from '../../../../config/statusConfig';
import type { RsvpStatus } from '../../../../types';

interface WorkshopSessionInfoTabProps {
  cohortId: string;
  workshopId: string;
  workshopTitle: string;
  sessionId?: string;
  /** Whether attendance tracking is enabled for this workshop (MED-574). Default: true */
  trackAttendance?: boolean;
  /**
   * When false (the coach page), no add/remove of resources and no "Add Participants":
   * the API refuses a coach both. Removing a participant stays (MED-1361). Default: true
   */
  canManageResources?: boolean;
}

export function WorkshopSessionInfoTab({
  cohortId,
  workshopId,
  workshopTitle,
  sessionId,
  trackAttendance = true,
  canManageResources = true,
}: WorkshopSessionInfoTabProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<WorkshopParticipant[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [participantsKey, setParticipantsKey] = useState(0);
  const [addResourceModalOpen, setAddResourceModalOpen] = useState(false);
  const [resourcesKey, setResourcesKey] = useState(0);
  const [resourceCount, setResourceCount] = useState(0);
  const [allSessions, setAllSessions] = useState<Session[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);

  const loadParticipants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await workshopsApi.listParticipants(cohortId, workshopId);
      setParticipants(data);
    } catch {
      setError('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [cohortId, workshopId, participantsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  // Fetch sessions and categories for the resource modal
  useEffect(() => {
    Promise.all([
      sessionsApi.list(cohortId),
      resourcesApi.getCategories(),
    ]).then(([sessions, cats]) => {
      setAllSessions(sessions);
      setCategories(cats);
    }).catch(() => {
      // Non-critical — resource modal will have no session/category options
    });
  }, [cohortId]);

  const handleParticipantsAdded = () => {
    setParticipantsKey((k) => k + 1);
  };

  const handleResourceAdded = () => {
    setResourcesKey((k) => k + 1);
  };

  // MED-969: Remove participant state
  const [removeTarget, setRemoveTarget] = useState<WorkshopParticipant | null>(null);
  const [removeModalOpened, { open: openRemoveModal, close: closeRemoveModal }] = useDisclosure(false);
  const [removing, setRemoving] = useState(false);

  const handleRemoveClick = (participant: WorkshopParticipant) => {
    setRemoveTarget(participant);
    openRemoveModal();
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await workshopsApi.removeParticipant(cohortId, workshopId, removeTarget.user_id);
      notifications.show({
        message: `${removeTarget.first_name} ${removeTarget.last_name} removed from workshop`,
        color: 'green',
      });
      closeRemoveModal();
      setRemoveTarget(null);
      setParticipantsKey((k) => k + 1);
    } catch {
      notifications.show({ message: 'Failed to remove participant', color: 'red' });
    } finally {
      setRemoving(false);
    }
  };

  const participantsContent = loading ? (
    <Center py="xl">
      <Loader size="sm" />
    </Center>
  ) : error ? (
    <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
      {error}
    </Alert>
  ) : participants.length === 0 ? (
    <Stack align="center" py="xl" gap="sm">
      <Users size={32} color="#bbb" />
      <Text size="sm" c="dimmed">No participants assigned</Text>
      {canManageResources && (
        <Button
          size="sm"
          variant="light"
          leftSection={<Plus size={14} />}
          onClick={() => setAddModalOpen(true)}
        >
          Add Participants
        </Button>
      )}
    </Stack>
  ) : (
    <Stack gap="xs">
      <Table striped highlightOnHover withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Participant</Table.Th>
            <Table.Th>Group</Table.Th>
            <Table.Th>RSVP</Table.Th>
            <Table.Th w={40} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {participants.map((p) => {
            const rsvpCfg =
              RSVP_STATUS_CONFIG[(p.rsvp_status as RsvpStatus) || 'pending'] ||
              RSVP_STATUS_CONFIG.pending;
            const isWithdrawn = p.participant_status === 'withdrawn';
            return (
              <Table.Tr
                key={p.attendance_id}
                style={isWithdrawn ? { opacity: 0.55 } : undefined}
              >
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <ParticipantAvatar
                      firstName={p.first_name}
                      lastName={p.last_name}
                      size={28}
                    />
                    <div>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={500}>{p.first_name} {p.last_name}</Text>
                        {isWithdrawn && <WithdrawnPill withdrawnAt={p.withdrawn_at} />}
                      </Group>
                      <Text size="xs" c="dimmed">{p.email}</Text>
                    </div>
                  </Group>
                </Table.Td>
                <Table.Td>
                  {p.group_name ? (
                    <Badge
                      size="xs"
                      variant="light"
                      color="medleyGreen"
                      tt="none"
                      leftSection={
                        <Box
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: getGroupAvatarColor(p.group_name, p.group_id || ''),
                          }}
                        />
                      }
                    >
                      {p.group_name}
                    </Badge>
                  ) : (
                    <Badge
                      size="xs"
                      variant="light"
                      color="yellow"
                      tt="none"
                      leftSection={
                        <Box
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: 'currentColor',
                          }}
                        />
                      }
                    >
                      Unassigned
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {isWithdrawn ? (
                    <Text size="sm" c="dimmed">—</Text>
                  ) : (
                    <Badge size="xs" color={rsvpCfg.color} variant="light">
                      {rsvpCfg.label}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {!isWithdrawn && (
                    <Tooltip label="Remove from workshop" position="left" withArrow>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemoveClick(p)}
                      >
                        <UserMinus size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <ThemeIcon
          size="xs"
          variant="filled"
          color={trackAttendance ? 'medleyGreen' : 'gray'}
          radius="xl"
        >
          {trackAttendance ? <Check size={10} /> : <X size={10} />}
        </ThemeIcon>
        <Text size="sm" c="dimmed">
          Attendance tracking: <Text span fw={500} c={trackAttendance ? 'medleyGreen' : 'dimmed'}>
            {trackAttendance ? 'Enabled' : 'Disabled'}
          </Text>
        </Text>
      </Group>

      <SectionAccordion
        title={(() => {
          if (participants.length === 0) return 'Participants';
          const activeCount = participants.filter((p) => p.participant_status !== 'withdrawn').length;
          const withdrawnCount = participants.length - activeCount;
          return (
            <>
              Participants ({activeCount})
              {withdrawnCount > 0 && (
                <Text span size="xs" c="dimmed" ml={6} fw={400}>
                  +{withdrawnCount} withdrawn
                </Text>
              )}
            </>
          );
        })()}
        defaultExpanded
        rightSection={
          participants.length > 0 && canManageResources ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<Plus size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                setAddModalOpen(true);
              }}
            >
              Add Participants
            </Button>
          ) : undefined
        }
      >
        {participantsContent}
      </SectionAccordion>

      {sessionId && (
        // MED-1393: remount open after a save, so the new resource shows.
        <SectionAccordion
          key={resourcesKey}
          title={
            <>
              Resources
              {resourceCount > 0 && (
                <Text span size="xs" c="dimmed" fw={400} ml={8}>
                  {resourceCount} resource{resourceCount !== 1 ? 's' : ''}
                </Text>
              )}
            </>
          }
          defaultExpanded={resourcesKey > 0}
          rightSection={
            canManageResources ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<Plus size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddResourceModalOpen(true);
                }}
              >
                Add Resource
              </Button>
            ) : undefined
          }
        >
          <ResourcesSection
            key={resourcesKey}
            cohortId={cohortId}
            sessionId={sessionId}
            categories={categories}
            onResourceAdded={handleResourceAdded}
            onCountChange={setResourceCount}
            readOnly={!canManageResources}
          />
        </SectionAccordion>
      )}

      <AddWorkshopParticipantsModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        cohortId={cohortId}
        workshopId={workshopId}
        workshopTitle={workshopTitle}
        onAdded={handleParticipantsAdded}
      />

      {canManageResources && sessionId && addResourceModalOpen && (
        <AddResourceModal
          opened={addResourceModalOpen}
          onClose={() => setAddResourceModalOpen(false)}
          cohortId={cohortId}
          sessions={allSessions}
          categories={categories}
          onCreated={handleResourceAdded}
          defaultSessionIds={[sessionId]}
        />
      )}

      {/* MED-969: Remove participant confirmation modal */}
      <Modal
        opened={removeModalOpened}
        onClose={closeRemoveModal}
        title="Remove Participant"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to remove{' '}
            <Text span fw={600}>{removeTarget?.first_name} {removeTarget?.last_name}</Text>{' '}
            from <Text span fw={600}>{workshopTitle}</Text>?
          </Text>
          <Text size="sm" c="dimmed">
            They will remain in the cohort and any other workshops they are assigned to.
            If the workshop has been finalized, their calendar invite will be cancelled.
          </Text>
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={closeRemoveModal} disabled={removing}>
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<UserMinus size={14} />}
              onClick={handleRemoveConfirm}
              loading={removing}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default WorkshopSessionInfoTab;
